import type { LocationQuery } from 'vue-router';

import type { LogoutType, Session } from 'shared/clients/client-spa-AccountGroup';
import type { ServiceID as ServiceIdUser } from 'shared/src/models/serviceIdEU.model';
import type { ServiceID as ServiceIdOrganization } from 'shared/src/models/serviceIdKO.model';

enum THEME {
  LIGHT = 'light',
  DARK = 'dark',
  SYSTEM = 'system',
}

export type Action = (payload?: { [key: string]: any }) => Promise<void>;

export interface MessageConfig {
  messageType: MESSAGE_TYPE;
  destinationApp: DESTINATION_APP_TYPE;
  action: (payload: MessagePayloadMapByMessageTypeUnion, eventMessage: MessageEvent<MessageData>) => Promise<void>;
}

export interface MessageData {
  messageType: MESSAGE_TYPE,
  destinationApp: DESTINATION_APP_TYPE
  payload: MessagePayloadMapByMessageTypeUnion;
}

export enum MESSAGE_TYPE {
  REDIRECT = 'redirect',
  UPDATE_ACTIVE_SERVICE_ID = 'updateActiveServiceId',
  WEB_INITIALIZED = 'webInitialized',
  REFRESH_TOKEN_INJECTION = 'refreshTokenInjection',
  TOKEN_AND_LANGUAGE_INJECTION = 'tokenAndLanguageInjection',
  TOKEN_AND_LANGUAGE_AND_THEME_INJECTION = 'tokenAndLanguageAndThemeInjection',
  MOBILE_LANGUAGE_INJECTION = 'mobileLanguageInjection',
  LANGUAGE_INJECTION = 'languageInjection',
  LOGOUT = 'logout',
  TOKEN_INJECTION = 'tokenInjection',
  LOGOUT_FROM_MOBILE_APP = 'logoutFromMobileApp',
  MOBILE_CHECK_SESSION = 'mobileCheckSession',
  STOP_CHECK_SESSION = 'stopCheckSession',
  START_CHECK_SESSION = 'startCheckSession',
  UPDATE_THEME = 'updateTheme'
}

export enum DESTINATION_APP_TYPE {
  HOST = 'host',
  E_DECLARATIONS = 'eDeclarations',
  E_DECLARATIONS_PARENT = 'eDeclarationsParent',
  MOBILE = 'mobile',
  ROUTE_ROOT = 'routeRoot',
  ANY_APP = 'anyApp',
}

export interface MobileCheckSession extends Session {
  appVersion: string;
}

type MessageTypeMapByDestinationAppType = {
  [DESTINATION_APP_TYPE.E_DECLARATIONS]: MESSAGE_TYPE.REDIRECT;
  [DESTINATION_APP_TYPE.E_DECLARATIONS_PARENT]: MESSAGE_TYPE.REDIRECT | MESSAGE_TYPE.UPDATE_ACTIVE_SERVICE_ID;
  [DESTINATION_APP_TYPE.HOST]:
    MESSAGE_TYPE.TOKEN_AND_LANGUAGE_INJECTION |
    MESSAGE_TYPE.LOGOUT |
    MESSAGE_TYPE.LANGUAGE_INJECTION |
    MESSAGE_TYPE.MOBILE_LANGUAGE_INJECTION |
    MESSAGE_TYPE.TOKEN_AND_LANGUAGE_AND_THEME_INJECTION |
    MESSAGE_TYPE.REFRESH_TOKEN_INJECTION |
    MESSAGE_TYPE.TOKEN_INJECTION |
    MESSAGE_TYPE.LOGOUT_FROM_MOBILE_APP |
    MESSAGE_TYPE.UPDATE_THEME |
    MESSAGE_TYPE.MOBILE_CHECK_SESSION;
  [DESTINATION_APP_TYPE.MOBILE]: MESSAGE_TYPE.WEB_INITIALIZED;
  [DESTINATION_APP_TYPE.ROUTE_ROOT]: MESSAGE_TYPE.REDIRECT;
  [DESTINATION_APP_TYPE.ANY_APP]: MESSAGE_TYPE.STOP_CHECK_SESSION | MESSAGE_TYPE.START_CHECK_SESSION;
}

export type ValidMessageType<T extends DESTINATION_APP_TYPE> = MessageTypeMapByDestinationAppType[T];

export type MessagePayloadMapByMessageType = {
  [MESSAGE_TYPE.REDIRECT]: { redirectUrl: string; query?: LocationQuery; };
  [MESSAGE_TYPE.UPDATE_ACTIVE_SERVICE_ID]: { serviceId: ServiceIdUser | ServiceIdOrganization | null }
  [MESSAGE_TYPE.WEB_INITIALIZED]: null | undefined;
  [MESSAGE_TYPE.TOKEN_AND_LANGUAGE_INJECTION]: { token: string, language: string };
  [MESSAGE_TYPE.TOKEN_AND_LANGUAGE_AND_THEME_INJECTION]: { token: string, language: string, theme: THEME };
  [MESSAGE_TYPE.UPDATE_THEME]: THEME;
  [MESSAGE_TYPE.MOBILE_LANGUAGE_INJECTION]: string;
  [MESSAGE_TYPE.LANGUAGE_INJECTION]: { language: string, omitUpdateUserLang: boolean };
  [MESSAGE_TYPE.LOGOUT]: LogoutType;
  [MESSAGE_TYPE.REFRESH_TOKEN_INJECTION]: string;
  [MESSAGE_TYPE.TOKEN_INJECTION]: string;
  [MESSAGE_TYPE.LOGOUT_FROM_MOBILE_APP]: null | undefined;
  [MESSAGE_TYPE.MOBILE_CHECK_SESSION]: MobileCheckSession;
  [MESSAGE_TYPE.STOP_CHECK_SESSION]: null | undefined;
  [MESSAGE_TYPE.START_CHECK_SESSION]: null | undefined;
}

export type MessagePayloadMapByMessageTypeUnion = MessagePayloadMapByMessageType[keyof MessagePayloadMapByMessageType];

export type MessagePayload<T extends MESSAGE_TYPE> = MessagePayloadMapByMessageType[T];